import { useEffect, useMemo, useState } from 'preact/hooks';
import { api, onEntry, type TokenDetail, type TokenSummary } from '../api.js';
import { Avatar, Meter, Price, toast, useTicker, copy } from '../components/bits.js';
import { Chart } from '../components/Chart.js';
import { Receipt } from '../components/Receipt.js';
import { PnlCard } from '../components/PnlCard.js';
import { zec, tokens, parseZec, parseTokens, ago, short, usd, tokensExact } from '../format.js';
import { quoteBuy, quoteSell, applyBuy, maxBuyWithin, type Pool } from '../../../src/core/curve.js';
import { send, wallet, accountForCoin, coinAccount, fund, notesZat, planFund, FRESH_NOTE_MS, NotEnoughPrivate } from '../wallet.js';
import { useStore, refreshAccount, myKeys, holdingsOf, accountZat } from '../store.js';
import { ApiError } from '../api.js';
import { baseFees, feesAt, feePct, walletCap, capActive, mmss, loadPrefs, savePrefs, type TradePrefs } from '../launch.js';
import type { GenesisConfig, LogEntry } from '../../../src/core/types.js';

export function TokenPage({ id }: { id: string }) {
  const [d, setD] = useState<TokenDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [tab, setTab] = useState<'trades' | 'holders'>('trades');
  const s = useStore();
  useTicker(10_000);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .token(id)
        .then((x) => alive && setD(x))
        .catch(() => alive && setMissing(true));
    void load();
    let t: ReturnType<typeof setTimeout> | undefined;
    const off = onEntry((e) => {
      const data = e.data as any;
      const b = data?.req?.body;
      const settled = e.kind === 'system' && data?.type === 'launch_settle' && data.token === id;
      if (settled || (b && (b.token === id || (b.type === 'create' && (e.result as any)?.token === id)))) {
        clearTimeout(t);
        t = setTimeout(load, 150);
      }
    });
    return () => {
      alive = false;
      off();
      clearTimeout(t);
    };
  }, [id]);

  if (missing) return <div class="empty">This coin does not exist. <a href="#/">See all coins</a></div>;
  if (!d) return <div class="empty">Loading…</div>;
  const t = d.token;
  return (
    <div class="token-page">
      <div class="tp-main">
        <div class="token-head">
          <Avatar token={t} size={72} />
          <div style={{ minWidth: 0 }}>
            <h1 class="ticker">${t.symbol}</h1>
            <div class="muted">
              {t.name}, launched {ago(t.createdTs)} ago by{' '}
              <button class="chip" onClick={() => copy(t.creator)} title={t.creator}>
                {short(t.creator)}
              </button>
            </div>
          </div>
        </div>
        {t.description && <p style={{ maxWidth: '70ch', marginTop: 0 }}>{t.description}</p>}
        {s.info && <LaunchBanner t={t} g={s.info.genesis} />}
        <div class="token-stats">
          <div class="stat">
            <div class="k">Market cap</div>
            <div class="v">
              {zec(t.marketCapZat, 2)} ZEC <span class="muted" style={{ fontSize: 16 }}>{usd(t.marketCapZat, s.zecUsd)}</span>
            </div>
          </div>
          <div class="stat">
            <div class="k">Price, ZEC</div>
            <div class="v">
              <Price zatPerToken={t.priceZat} />
            </div>
          </div>
          <div class="stat">
            <div class="k">24h volume</div>
            <div class="v">{zec(t.volume24hZat, 2)} ZEC</div>
          </div>
          <div class="stat">
            <div class="k">Trades</div>
            <div class="v">{t.trades}</div>
          </div>
        </div>
        <Chart tokenId={id} />
      </div>

      <div class="tp-list">
        <div class="tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'trades'} onClick={() => setTab('trades')}>
            Trades
          </button>
          <button role="tab" aria-selected={tab === 'holders'} onClick={() => setTab('holders')}>
            Top holders
          </button>
        </div>
        {tab === 'trades' ? (
          d.trades.length === 0 ? (
            <div class="empty">{t.launchOpen ? 'Launch minute: buys fill together at one price when it ends.' : 'No trades yet. The first buy sets the tone.'}</div>
          ) : (
            <table class="list">
              <thead>
                <tr>
                  <th>Trader</th>
                  <th>Side</th>
                  <th class="r">ZEC</th>
                  <th class="r">{t.symbol}</th>
                  <th class="r">When</th>
                  <th class="r">Entry</th>
                </tr>
              </thead>
              <tbody>
                {d.trades.slice(0, 50).map((tr) => (
                  <tr key={`${tr.seq}-${tr.user}`}>
                    <td>{myKeys().has(tr.user) ? 'You' : short(tr.user)}</td>
                    <td class={tr.side === 'buy' ? 'up' : 'down'}>{tr.side === 'buy' ? 'Buy' : 'Sell'}</td>
                    <td class="r">{zec(tr.zat)}</td>
                    <td class="r">{tokens(tr.tokens)}</td>
                    <td class="r muted">{ago(tr.ts)}</td>
                    <td class="r muted">#{tr.seq}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          <table class="list">
            <thead>
              <tr>
                <th>Holder</th>
                <th class="r">Amount</th>
                <th class="r">Share</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Bonding curve</td>
                <td class="r">{tokens(BigInt(d.pool.realTok) + BigInt(d.pool.reserveTok))}</td>
                <td class="r">{((Number(BigInt(d.pool.realTok) + BigInt(d.pool.reserveTok)) / 1e15) * 100).toFixed(1)}%</td>
              </tr>
              {d.holders.map((h) => (
                <tr key={h.user}>
                  <td>
                    {myKeys().has(h.user) ? 'You' : short(h.user)}
                    {h.user === t.creator ? <span class="muted"> (creator)</span> : null}
                  </td>
                  <td class="r">{tokens(h.amount)}</td>
                  <td class="r">{((Number(h.amount) / 1e15) * 100).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <aside class="tp-side">
        <TradePanel d={d} />
        <div class="panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong>{t.graduated ? 'Graduated' : 'Bonding curve'}</strong>
            <span class="muted">{(t.progressBps / 100).toFixed(1)}%</span>
          </div>
          <Meter bps={t.progressBps} graduated={t.graduated} />
          <p class="muted" style={{ fontSize: 13, margin: '10px 0 0' }}>
            {t.graduated
              ? 'The curve sold out. This coin now trades in an open pool backed by the ZEC it raised and the reserved supply.'
              : `${tokens(d.pool.realTok)} ${t.symbol} left on the curve, ${zec(d.pool.realZat, 3)} ZEC raised. When the curve sells out the coin graduates to an open pool.`}
          </p>
        </div>
        <SafetyPanel d={d} />
        <PnlCard d={d} />
      </aside>
    </div>
  );
}

/** Live state of the launch rules: the fair minute, the falling anti-snipe fee, the wallet cap. */
function LaunchBanner({ t, g }: { t: TokenSummary; g: GenesisConfig }) {
  useTicker(1000);
  const now = Date.now();
  if (t.launchOpen && now < t.launchEnd)
    return (
      <div class="notice launch" role="status">
        <strong>Fair launch: {mmss(t.launchEnd - now)} left.</strong> Every buy in the first minute fills at one price when it ends, so being
        first or using many wallets gains nothing. {t.launchOrders} {t.launchOrders === 1 ? 'buyer' : 'buyers'}, {zec(t.launchZat, 4)} ZEC in so
        far. Selling opens after the minute.
      </div>
    );
  if (t.launchOpen) return <div class="notice launch" role="status">Filling the launch minute at one price…</div>;
  const parts: string[] = [];
  if (now < t.snipeUntil && feePct(g, t, now) > 1.01)
    parts.push(`Anti-snipe fee ${feePct(g, t, now).toFixed(1)}% now, back to 1% in ${mmss(t.snipeUntil - now)}. The extra stays in the coin.`);
  if (capActive(g, t, now)) parts.push(`No wallet may hold more than ${g.walletCapBps / 100}% of ${t.symbol} for ${mmss(t.capUntil - now)} more.`);
  if (!parts.length) return null;
  return (
    <div class="notice warn" role="status" style={{ marginBottom: 16 }}>
      {parts.join(' ')}
    </div>
  );
}

/** Safety at a glance, from aggregates only: nobody's address is shown. */
function SafetyPanel({ d }: { d: TokenDetail }) {
  const t = d.token;
  const pct = (bps: number) => `${(bps / 100).toFixed(bps < 100 ? 2 : 1)}%`;
  const rows: [string, string, boolean][] = [
    ['Holders', String(d.safety.holders), false],
    ['Top 10 hold', pct(d.safety.top10Bps), d.safety.top10Bps > 5000],
    ['Largest holder', pct(d.safety.largestBps), d.safety.largestBps > 1000],
    ['Creator holds', pct(t.creatorHoldsBps), t.creatorHoldsBps > 500],
    ['Bought in the launch minute', t.launchOpen ? 'filling…' : pct(t.launchBoughtBps), false],
    ['Creator earned', `${zec(t.creatorFeesZat, 6)} ZEC`, false],
  ];
  return (
    <div class="panel">
      <strong>Safety</strong>
      <table class="list kv" style={{ marginTop: 8 }}>
        <tbody>
          {rows.map(([k, v, warn]) => (
            <tr key={k}>
              <td class="muted">{k}</td>
              <td class={`r${warn ? ' down' : ''}`}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p class="muted" style={{ fontSize: 13, margin: '10px 0 0' }}>
        Can't happen here: nobody can mint more {t.symbol}, nobody can pull the pool's liquidity, and no wallet could hold more than 3% in the
        first hour. Holders are counted, never named.
      </p>
    </div>
  );
}

function TradePanel({ d }: { d: TokenDetail }) {
  const s = useStore();
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [prefs, setPrefsState] = useState<TradePrefs>(loadPrefs);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [last, setLast] = useState<LogEntry | null>(null);
  // Buying straight from the main account: works with a deposit seconds old, but the buy is linked to that deposit.
  const [fromMain, setFromMain] = useState(false);
  useTicker(d.token.launchOpen || Date.now() < d.token.snipeUntil ? 1000 : 15_000);
  const t = d.token;
  const g = s.info?.genesis;
  const setPrefs = (p: TradePrefs) => {
    setPrefsState(p);
    savePrefs(p);
  };
  // Sells come from the account that holds the most of this coin; buys from the coin's own account, topped up from private notes.
  const sellFrom = holdingsOf(t.id)[0];
  const holding = sellFrom?.balance ?? 0n;
  const coinIndex = fromMain ? 0 : coinAccount(t.id);
  const coinZat = coinIndex === undefined ? 0n : accountZat(coinIndex);
  const coinAcc = coinIndex === undefined ? undefined : s.accounts.find((a) => a.index === coinIndex);
  const coinHolding = BigInt(coinAcc?.data.tokens?.find((x) => x.id === t.id)?.balance ?? '0');
  const committed = BigInt(coinAcc?.data.launchOrders?.find((o) => o.token === t.id)?.zat ?? '0');
  const mainZat = BigInt(s.account?.zat ?? '0');
  const balance = fromMain ? mainZat : coinZat + notesZat();
  const now = Date.now();

  const pool: Pool = useMemo(
    () => ({
      mode: t.mode,
      vZat: BigInt(d.pool.vZat),
      vTok: BigInt(d.pool.vTok),
      realZat: BigInt(d.pool.realZat),
      realTok: BigInt(d.pool.realTok),
      reserveTok: BigInt(d.pool.reserveTok),
    }),
    [d],
  );

  const quoteFor = (sideX: 'buy' | 'sell', value: bigint | null) => {
    if (!g || !value || value <= 0n) return { quote: null, error: '' };
    try {
      if (sideX === 'buy' && t.launchOpen) {
        // Launch minute: this order's share of everything put in so far, at the one price.
        const total = BigInt(t.launchZat) + value;
        const q = quoteBuy(pool, total, baseFees(g));
        return { quote: { out: (q.tokensOut * value) / total, fee: ((q.fee + q.creatorFee) * value) / total, snipe: 0 }, error: '' };
      }
      if (sideX === 'sell' && t.launchOpen) return { quote: null, error: 'Selling opens when the launch minute is over.' };
      const fees = feesAt(g, t, now);
      const snipe = Number(fees.poolBps ?? 0n) / 100;
      if (sideX === 'buy') {
        const q = quoteBuy(pool, value, fees);
        return { quote: { out: q.tokensOut, fee: q.fee + q.creatorFee + q.poolFee, refund: q.refund, snipe }, error: '' };
      }
      const q = quoteSell(pool, value, fees);
      return { quote: { out: q.zatOut, fee: q.fee + q.creatorFee + q.poolFee, snipe }, error: '' };
    } catch {
      return { quote: null, error: 'Amount too small' };
    }
  };

  const parsed = side === 'buy' ? parseZec(amount) : parseTokens(amount);
  const { quote, error: quoteErr } = quoteFor(side, parsed);
  const tooMuch = parsed !== null && (side === 'buy' ? parsed > balance : parsed > holding);

  // The 3% wallet cap, checked here so nobody signs a request that would be refused.
  const cap = g ? walletCap(g) : null;
  let capErr = '';
  if (g && cap !== null && side === 'buy' && parsed && quote && capActive(g, t, now)) {
    if (t.launchOpen) {
      const room = cap - coinHolding;
      const max = room > 0n ? maxBuyWithin(pool, room, baseFees(g)) : 0n;
      if (max !== null && committed + parsed > max)
        capErr = `In the launch minute one wallet may put in at most ${zec(max, 6)} ZEC (${g.walletCapBps / 100}% of the coin)${committed > 0n ? `; you already put in ${zec(committed, 6)}` : ''}.`;
    } else if (coinHolding + quote.out > cap) {
      capErr = `No wallet may hold more than ${g.walletCapBps / 100}% of ${t.symbol} until ${new Date(t.capUntil).toLocaleTimeString()}.`;
    }
  }
  // Notes made minutes ago and spent now can be linked by timing; say so (it does not block the buy).
  const plan = side === 'buy' && !fromMain && parsed && parsed > 0n && !tooMuch ? planFund(parsed, coinZat) : null;
  const freshMinutes = plan && plan.youngestMs < FRESH_NOTE_MS ? Math.max(1, Math.round(plan.youngestMs / 60_000)) : null;

  const submit = async (value: bigint | null, sideX: 'buy' | 'sell' = side) => {
    setErr('');
    if (!wallet()) {
      location.hash = '#/wallet';
      return;
    }
    const { quote: q } = quoteFor(sideX, value);
    if (!value || !q) return;
    // One-tap buys skip the form, so the same checks run here.
    if (sideX === 'buy' && (value > balance || (capErr && value === parsed))) return;
    if (sideX === 'sell' && value > holding) return;
    let min = (q.out * BigInt(Math.round((100 - prefs.slippage) * 100))) / 10_000n;
    if (sideX === 'buy' && t.launchOpen && g) {
      // Inside the launch minute the minimum is ignored (everyone gets one price). If the order arrives just after it,
      // it becomes an ordinary buy at the steepest anti-snipe fee; this minimum is what protects it then.
      try {
        const batch = BigInt(t.launchZat);
        const after = batch > 0n ? applyBuy(pool, quoteBuy(pool, batch, baseFees(g))) : pool;
        const late = quoteBuy(after, value, feesAt(g, t, t.launchEnd));
        min = (late.tokensOut * BigInt(Math.round((100 - prefs.slippage) * 100))) / 10_000n;
      } catch {
        min = 0n;
      }
    }
    setBusy(true);
    try {
      let receipt: LogEntry;
      if (sideX === 'buy' && fromMain) {
        receipt = await send({ type: 'buy', token: t.id, zat: value.toString(), minTokens: min.toString() }, 0);
      } else if (sideX === 'buy') {
        const idx = accountForCoin(t.id);
        await fund(idx, value, accountZat(idx));
        receipt = await send({ type: 'buy', token: t.id, zat: value.toString(), minTokens: min.toString() }, idx);
      } else {
        receipt = await send({ type: 'sell', token: t.id, tokens: value.toString(), minZat: min.toString() }, sellFrom?.index ?? 0);
      }
      setLast(receipt);
      setAmount('');
      const r = receipt.result as { batched?: boolean };
      toast(r.batched ? `Order in: fills at one price in ${mmss(t.launchEnd - Date.now())}` : sideX === 'buy' ? `Bought $${t.symbol}` : `Sold $${t.symbol}`);
      refreshAccount();
    } catch (x) {
      const msg =
        x instanceof NotEnoughPrivate
          ? 'Not enough private ZEC. Make ZEC private on the Wallet page first.'
          : x instanceof ApiError && x.code === 'slippage'
            ? 'The price moved more than your slippage allows. Try again or raise slippage.'
            : x instanceof ApiError && x.code === 'insufficient'
              ? sideX === 'buy'
                ? 'Not enough ZEC. Deposit more in your wallet.'
                : `You don't hold that many ${t.symbol}.`
              : (x as Error).message;
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const pick = (v: string, sideX: 'buy' | 'sell') => {
    if (prefs.instant && !busy) void submit(sideX === 'buy' ? parseZec(v) : parseTokens(v), sideX);
    else setAmount(v);
  };
  const sellQuick: [string, string][] = [
    [tokensExact(holding / 4n), '25%'],
    [tokensExact(holding / 2n), '50%'],
    [tokensExact(holding), '100%'],
  ];

  return (
    <div class="panel">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit(parsed);
        }}
      >
        <div class="side-toggle" role="group" aria-label="Buy or sell">
          <button
            type="button"
            class="b"
            aria-pressed={side === 'buy'}
            onClick={() => {
              setSide('buy');
              setAmount('');
            }}
          >
            Buy
          </button>
          <button
            type="button"
            class="s"
            aria-pressed={side === 'sell'}
            onClick={() => {
              setSide('sell');
              setAmount('');
            }}
          >
            Sell
          </button>
        </div>
        <div class="field">
          <label for="amt">{side === 'buy' ? 'You pay' : 'You sell'}</label>
          <div class="input">
            <input id="amt" inputMode="decimal" autocomplete="off" placeholder="0.0" value={amount} onInput={(e) => setAmount((e.target as HTMLInputElement).value)} />
            <span class="unit">{side === 'buy' ? 'ZEC' : t.symbol}</span>
          </div>
          <div class="hint">
            {side === 'buy'
              ? fromMain
                ? `Main account ${zec(balance)} ZEC`
                : `Private balance ${zec(balance)} ZEC`
              : `You hold ${tokens(holding)} ${t.symbol}`}
          </div>
        </div>
        {side === 'buy' && mainZat > 0n && (
          <label class="instant">
            <input type="checkbox" checked={fromMain} onChange={(e) => setFromMain((e.target as HTMLInputElement).checked)} /> Buy from my main
            account: right after a deposit, no making private first, but not private
          </label>
        )}
        {editing ? (
          <div class="quick">
            <input
              class="preset-edit"
              aria-label="Buy presets in ZEC, separated by commas"
              value={draft}
              onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
            />
            <button
              type="button"
              class="chip"
              onClick={() => {
                const list = draft
                  .split(/[ ,;]+/)
                  .map((x) => x.trim().replace(',', '.'))
                  .filter((x) => parseZec(x) !== null && parseZec(x)! > 0n)
                  .slice(0, 6);
                if (list.length) setPrefs({ ...prefs, presets: list });
                setEditing(false);
              }}
            >
              Save
            </button>
          </div>
        ) : (
          <div class="quick">
            {side === 'buy'
              ? prefs.presets.map((v) => (
                  <button type="button" class="chip" key={v} onClick={() => pick(v, 'buy')} disabled={busy}>
                    {v} ZEC
                  </button>
                ))
              : sellQuick.map(([v, label]) => (
                  <button type="button" class="chip" key={label} onClick={() => pick(v, 'sell')} disabled={busy || holding === 0n || t.launchOpen}>
                    {label}
                  </button>
                ))}
            {side === 'buy' && (
              <button
                type="button"
                class="chip ghost-chip"
                onClick={() => {
                  setDraft(prefs.presets.join(', '));
                  setEditing(true);
                }}
              >
                Edit
              </button>
            )}
          </div>
        )}
        <label class="instant">
          <input type="checkbox" checked={prefs.instant} onChange={(e) => setPrefs({ ...prefs, instant: (e.target as HTMLInputElement).checked })} />{' '}
          One tap: the buttons above trade at once
        </label>
        {quote && (
          <div class="quote">
            <div>
              <span class="muted">{t.launchOpen && side === 'buy' ? 'You get about (if the minute closed now)' : 'You get about'}</span>
              <strong>{side === 'buy' ? `${tokens(quote.out)} ${t.symbol}` : `${zec(quote.out, 6)} ZEC`}</strong>
            </div>
            <div>
              <span class="muted">{quote.snipe > 0 ? `Fee, incl. ${quote.snipe.toFixed(1)}% anti-snipe` : 'Fee'}</span>
              <span>{zec(quote.fee, 6)} ZEC</span>
            </div>
            {'refund' in quote && quote.refund ? (
              <div>
                <span class="muted">Unused, stays in your balance</span>
                <span>{zec(quote.refund, 6)} ZEC</span>
              </div>
            ) : null}
            {!(t.launchOpen && side === 'buy') && (
              <div>
                <span class="muted">Slippage</span>
                <span>
                  {[1, 5, 15].map((v) => (
                    <button type="button" class="chip" aria-pressed={prefs.slippage === v} onClick={() => setPrefs({ ...prefs, slippage: v })} key={v} style={{ marginLeft: 4 }}>
                      {v}%
                    </button>
                  ))}
                </span>
              </div>
            )}
          </div>
        )}
        {quoteErr && (
          <div class="field">
            <span class="err">{quoteErr}</span>
          </div>
        )}
        {capErr && (
          <div class="field">
            <span class="err">{capErr}</span>
          </div>
        )}
        {freshMinutes !== null && (
          <div class="notice warn" style={{ marginBottom: 12, fontSize: 13 }}>
            This buy uses private ZEC made {freshMinutes} min ago. Anyone reading the log could link it to your deposit by timing. Waiting
            longer is safer.
          </div>
        )}
        {fromMain && side === 'buy' && (
          <div class="notice warn" style={{ marginBottom: 12, fontSize: 13 }}>
            Anyone reading the public log can see that this buy comes from the account you deposited into. For private trading, make your ZEC
            private first on the Wallet page and buy without this box ticked.
          </div>
        )}
        {tooMuch && (
          <div class="field">
            <span class="err">
              {side === 'sell' ? (
                'More than you hold.'
              ) : fromMain ? (
                'More than your main account holds.'
              ) : mainZat > 0n ? (
                <>
                  More than your private balance. <a href="#/wallet">Make your ZEC private</a> first.
                </>
              ) : (
                'More than your private balance.'
              )}
            </span>
          </div>
        )}
        {err && (
          <div class="notice bad" style={{ marginBottom: 12 }}>
            {err}
          </div>
        )}
        {!wallet() ? (
          <a class="btn brass block" href="#/wallet">
            Set up a wallet to trade
          </a>
        ) : (
          <button class={`btn block ${side}`} disabled={busy || !quote || tooMuch || !!capErr}>
            {busy ? 'Signing…' : side === 'buy' ? (t.launchOpen ? `Join the launch of $${t.symbol}` : `Buy $${t.symbol}`) : `Sell $${t.symbol}`}
          </button>
        )}
      </form>
      {last && <Receipt entry={last} symbol={t.symbol} printing key={last.seq} />}
    </div>
  );
}
